'use client';

import { useCallback, useMemo } from 'react';
import {
  TimelineRoot,
  Text,
  type TimelineDetailRenderer,
  type TimelineStepData,
} from '@terra/ui-design-system';
import { Box, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type {
  ScenarioDiscoverySection,
  ScenarioStepTimelineItem,
} from './scenario-step-timeline.util';

type ScenarioStepsTimelineProps = {
  items: ScenarioStepTimelineItem[];
};

type ScenarioStepDetailData = ScenarioStepTimelineItem;

const resolveStepHeadline = (item: ScenarioStepTimelineItem): string => {
  if (item.technique && item.technique !== item.title) {
    return `${item.title} · ${item.technique}`;
  }

  return item.title;
};

const hasExpandableDetails = (item: ScenarioStepTimelineItem): boolean =>
  Boolean(
    item.description ||
      item.command ||
      item.prerequisites ||
      item.expectedOutput ||
      item.weaknessExplanation,
  );

const ScenarioStepNumberIcon = ({ stepNumber }: { stepNumber: number }) => (
  <Text fontSize="xs" fontWeight="medium" color="fg.muted">
    {stepNumber}
  </Text>
);

const ScenarioStepMetaField = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <Box w="full">
    <Text fontSize="xs" fontWeight="semibold" color="fg.default">
      {label}
    </Text>
    <Text fontSize="xs" color="fg.muted" mt={0.5} lineHeight="1.5">
      {value}
    </Text>
  </Box>
);

const ScenarioStepDetailContent = ({
  item,
}: {
  item: ScenarioStepTimelineItem;
}) => {
  const t = useTranslations('internalNetwork.scenarios.stepFields');
  const hasMeta = Boolean(item.prerequisites || item.expectedOutput);
  const hasLeadContent = Boolean(item.description || item.command);
  const weaknessLabel = item.weaknessName
    ? item.mitreTechniqueId
      ? `${item.weaknessName} · ${item.mitreTechniqueId}`
      : item.weaknessName
    : null;

  return (
    <VStack alignItems="stretch" gap={3} w="full">
      {item.description ? (
        <Text fontSize="sm" color="fg.muted" lineHeight="1.5">
          {item.description}
        </Text>
      ) : null}
      {weaknessLabel ? (
        <ScenarioStepMetaField label={t('weakness')} value={weaknessLabel} />
      ) : null}
      {item.weaknessExplanation ? (
        <ScenarioStepMetaField
          label={t('whyItWorks')}
          value={item.weaknessExplanation}
        />
      ) : null}
      {item.command ? (
        <Box
          as="pre"
          fontSize="xs"
          fontFamily="mono"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          bg="bg.subtle"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="md"
          p={2}
          color="fg.default"
        >
          {item.command}
        </Box>
      ) : null}
      {hasMeta ? (
        <VStack
          alignItems="stretch"
          gap={2}
          w="full"
          pt={hasLeadContent ? 1 : 0}
          borderTopWidth={hasLeadContent ? '1px' : undefined}
          borderTopColor={hasLeadContent ? 'border.subtle' : undefined}
        >
          {item.prerequisites ? (
            <ScenarioStepMetaField
              label={t('prerequisites')}
              value={item.prerequisites}
            />
          ) : null}
          {item.expectedOutput ? (
            <ScenarioStepMetaField
              label={t('expectedOutput')}
              value={item.expectedOutput}
            />
          ) : null}
        </VStack>
      ) : null}
    </VStack>
  );
};

const buildTimelineSteps = ({
  items,
  startIndex,
}: {
  items: ScenarioStepTimelineItem[];
  startIndex: number;
}): TimelineStepData<ScenarioStepDetailData>[] =>
  items.map((item, index) => {
    const stepNumber = startIndex + index + 1;
    const expandable = hasExpandableDetails(item);

    return {
      id: item.id,
      title: resolveStepHeadline(item),
      icon: <ScenarioStepNumberIcon stepNumber={stepNumber} />,
      ...(expandable
        ? {
            details: [
              {
                data: item,
              },
            ],
          }
        : {}),
    };
  });

const discoverySectionLabelKey: Record<
  ScenarioDiscoverySection,
  'preflight' | 'collection' | 'evidence'
> = {
  preflight: 'preflight',
  collection: 'collection',
  evidence: 'evidence',
};

export function ScenarioStepsTimeline({ items }: ScenarioStepsTimelineProps) {
  const tDiscovery = useTranslations('internalNetwork.scenarios.discovery');

  const groupedItems = useMemo(() => {
    const hasDiscoverySections = items.some((item) => item.discoverySection);
    if (!hasDiscoverySections) {
      return [{ section: null as ScenarioDiscoverySection | null, items }];
    }

    const sections: Array<{
      section: ScenarioDiscoverySection;
      items: ScenarioStepTimelineItem[];
    }> = [];

    for (const item of items) {
      const section = item.discoverySection ?? 'collection';
      const existing = sections.find((entry) => entry.section === section);
      if (existing) {
        existing.items.push(item);
      } else {
        sections.push({ section, items: [item] });
      }
    }

    return sections;
  }, [items]);

  const sectionStepOffsets = useMemo(() => {
    const offsets: number[] = [];
    let offset = 0;
    for (const group of groupedItems) {
      offsets.push(offset);
      offset += group.items.length;
    }
    return offsets;
  }, [groupedItems]);

  const renderStepDetail = useCallback<
    TimelineDetailRenderer<ScenarioStepDetailData>
  >(({ detail }) => {
    if (!detail.data) {
      return null;
    }

    return <ScenarioStepDetailContent item={detail.data} />;
  }, []);

  if (items.length === 0) {
    return null;
  }

  return (
    <VStack alignItems="stretch" gap={2} w="full" pt={1}>
      {groupedItems.map(({ section, items: sectionItems }, groupIndex) => {
        const timelineSteps = buildTimelineSteps({
          items: sectionItems,
          startIndex: sectionStepOffsets[groupIndex] ?? 0,
        });

        return (
          <Box key={section ?? 'default'} w="full">
            {section ? (
              <Text
                fontSize="xs"
                fontWeight="medium"
                color="fg.muted"
                textTransform="uppercase"
                letterSpacing="wider"
                mb={2}
                mt={section === 'preflight' ? 0 : 2}
              >
                {tDiscovery(discoverySectionLabelKey[section])}
              </Text>
            ) : null}
            <TimelineRoot
              steps={timelineSteps}
              timestampPlacement="outside"
              showOutsideTimestamp={false}
              detailRenderer={renderStepDetail}
            />
          </Box>
        );
      })}
    </VStack>
  );
}
